import {rules, type SecretMatch} from './rules';

export function redactSecrets(text: string, secrets: SecretMatch[]) {
  return secrets.reduce((str, secret) => {
    return str.replaceAll(secret.match, '•'.repeat(secret.match.length));
  }, text);
}

// Rules are authored without the `g` flag for `scanForSecrets`; `matchAll`
// requires global, so cache a global-flagged sibling per rule.
const GLOBAL_RULES: ReadonlyArray<RegExp> = Object.values(rules).map((regex) =>
  regex.global ? regex : new RegExp(regex.source, regex.flags + 'g')
);

/**
 * Replace every occurrence of every known secret kind in `text` with a
 * bullet run of the same length.
 */
export function redactAllSecrets(text: string): string {
  if (!text) return text;
  const matches = new Set<string>();
  for (const regex of GLOBAL_RULES) {
    for (const m of text.matchAll(regex)) {
      matches.add(m[0]);
    }
  }
  if (matches.size === 0) return text;
  // Longest first so a wrapping match (`OPENAI_KEY=sk-…`) is replaced before
  // the substring it contains (`sk-…`).
  const ordered = [...matches].sort((a, b) => b.length - a.length);
  let result = text;
  for (const match of ordered) {
    result = result.replaceAll(match, '•'.repeat(match.length));
  }
  return result;
}

export function redactDotEnvFile(
  content: string,
  redaction = '<redacted secret value>'
): string {
  const lines = content.split('\n');
  const redacted = lines.map((line) => {
    const trimmed = line.trim();
    if (trimmed.length === 0 || trimmed.startsWith('#')) {
      return line;
    }

    const quotedMatch = line.match(
      /^(\s*(?:export\s+)?[A-Za-z0-9_]*\s*=\s*)"(?:[^"]*)"(.*)$/
    );
    if (quotedMatch) {
      const [, prefix, suffix] = quotedMatch;
      return `${prefix}${redaction}${suffix}`;
    }

    const unquotedMatch = line.match(
      /^(\s*(?:export\s+)?[A-Za-z0-9_]*\s*=\s*)([^#\r\n]*?)(\s*)(#.*)?$/
    );
    if (unquotedMatch) {
      const [, prefix, , spaceBeforeComment, comment] = unquotedMatch;
      return `${prefix}${redaction}${spaceBeforeComment}${comment ?? ''}`;
    }
    return line;
  });
  return redacted.join('\n');
}
